import { type BoneMetadata } from './BoneAutoMapper'

/**
 * TripoMapper — direct bone-name mapping for Tripo3D auto-rigged humanoid GLBs.
 *
 * Tripo's convention is distinct from Mixamo/Quaternius: it uses `L_`/`R_` prefixes
 * (not Left/Right suffixes), separates the root-motion `Hip` bone from the structural
 * `Pelvis`, and emits many `*Twist01/02` helper bones. The neck and torso names are
 * also their own thing (`Waist`, `Spine01`, `Spine02`, `NeckTwist01`).
 *
 * Source: Mesh2Motion skeleton (e.g. `pelvis`, `spine_01`, `clavicle_l`)
 * Target: Tripo3D humanoid skeleton
 *
 * Bones intentionally not mapped (stay at bind pose during retarget):
 * - `Pelvis` — Tripo separates it from `Hip` but Mesh2Motion doesn't; we drive Hip
 *   only since it carries the root motion + body orientation
 * - `*Twist01`, `*Twist02` (calf, thigh, forearm, upperarm, neck) — twist helpers
 *   with no Mesh2Motion equivalent; leaving them at rest is the correct default
 * - `head_leaf`, `ball_l/r`, `ball_leaf_l/r` — Tripo's standard humanoid output
 *   does not include these; M2M tracks for them simply have no target
 * - all M2M finger bones — Tripo's standard humanoid does not include fingers
 *
 * Note on spine: M2M has spine_01/02/03; Tripo has Waist + Spine01 + Spine02. We
 * treat Waist as the lowermost spine and shift up (M2M spine_01 → Tripo Waist),
 * preserving the count.
 *
 * Reference bone list verified against an actual Tripo3D humanoid export
 * (provided by user, 2026-05).
 */
export class TripoMapper {
  private static readonly BONE_MAP: Record<string, string> = {
    // Torso — Hip is the root-motion bone in Tripo (parent of Pelvis); Pelvis is
    // a structural intermediate that we leave at rest. Spine maps lowermost-up.
    pelvis: 'Hip',
    spine_01: 'Waist',
    spine_02: 'Spine01',
    spine_03: 'Spine02',
    // Tripo names the neck "NeckTwist01" but it's the actual neck rotation bone,
    // not a twist helper (it's a parent of NeckTwist02 which is the helper).
    neck_01: 'NeckTwist01',
    head: 'Head',

    // Left arm
    clavicle_l: 'L_Clavicle',
    upperarm_l: 'L_Upperarm',
    lowerarm_l: 'L_Forearm',
    hand_l: 'L_Hand',

    // Right arm
    clavicle_r: 'R_Clavicle',
    upperarm_r: 'R_Upperarm',
    lowerarm_r: 'R_Forearm',
    hand_r: 'R_Hand',

    // Left leg
    thigh_l: 'L_Thigh',
    calf_l: 'L_Calf',
    foot_l: 'L_Foot',

    // Right leg
    thigh_r: 'R_Thigh',
    calf_r: 'R_Calf',
    foot_r: 'R_Foot'
  }

  /**
   * Detect a Tripo-shaped skeleton. Tripo's signature is `L_`/`R_` underscore-
   * prefixed limb bones — distinct from Mixamo's `LeftArm` (no separator) and
   * Quaternius's `LeftArm` (no separator). The detector requires:
   *  - no `mixamorig` prefix anywhere (hard disqualifier)
   *  - at least 2 `L_`-prefixed limb bones AND 2 `R_`-prefixed limb bones
   *
   * We deliberately DON'T require `Hip` + `Pelvis` together — Tripo can be
   * configured to omit the structural Pelvis (LOD pruning, custom rigs), and
   * loose detection plus a forgiving renamer is better than a hard miss.
   * The L_/R_ prefix is unambiguous against the other supported conventions.
   */
  static is_target_valid_skeleton (bone_names: string[]): boolean {
    const has_mixamo_prefix = bone_names.some(n => n.toLowerCase().includes('mixamorig'))
    if (has_mixamo_prefix) return false

    const known_left_limbs = ['L_Upperarm', 'L_Forearm', 'L_Hand', 'L_Thigh', 'L_Calf', 'L_Foot', 'L_Clavicle']
    const known_right_limbs = ['R_Upperarm', 'R_Forearm', 'R_Hand', 'R_Thigh', 'R_Calf', 'R_Foot', 'R_Clavicle']

    const left_count = known_left_limbs.filter(b => bone_names.includes(b)).length
    const right_count = known_right_limbs.filter(b => bone_names.includes(b)).length

    return left_count >= 2 && right_count >= 2
  }

  /** Mesh2Motion → Tripo. Returns undefined if the bone has no Tripo equivalent. */
  static map_source_bone_name_to_tripo (source_bone_name: string): string | undefined {
    return this.BONE_MAP[source_bone_name]
  }

  /** The full Tripo → Mesh2Motion mapping, used by the runtime track renamer. */
  static get_source_to_target_map (): Record<string, string> {
    const map: Record<string, string> = {}
    for (const [m2m, tripo] of Object.entries(this.BONE_MAP)) {
      map[tripo] = m2m
    }
    return map
  }

  /**
   * Map Mesh2Motion source bones onto a Tripo target skeleton (parallel to
   * `MixamoMapper.map_mixamo_bones`).
   * @returns Map of target bone name -> source bone name
   */
  static map_tripo_bones (source_bones: BoneMetadata[], target_bones: BoneMetadata[]): Map<string, string> {
    const mappings = new Map<string, string>()
    for (const source_bone of source_bones) {
      const expected_target_name = this.BONE_MAP[source_bone.name]
      if (expected_target_name === undefined) continue
      const target_bone = target_bones.find(tb => tb.name === expected_target_name)
      if (target_bone !== undefined) {
        mappings.set(target_bone.name, source_bone.name)
      }
    }
    console.log(`Tripo mapping complete: ${mappings.size} bones mapped`)
    return mappings
  }
}
